import React from 'react';
import { TemplateType } from '../TemplateType';
import usePdfContent, { PdfContentParams } from '../usePdfContent';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';

import { sqlDateToLabel } from 'helpers/helpers';
import PdfContent from 'apps/Pdf/components/common/PdfContent';
import Title from 'apps/Pdf/components/common/Title';
import BasicData from 'apps/Pdf/components/BasicData/BasicData';
import H1 from 'apps/Pdf/components/common/H1';
import H2 from 'apps/Pdf/components/common/H2';
import P from 'apps/Pdf/components/common/P';
import Signature from 'apps/Pdf/components/Signature/Signature';
import Loading from 'apps/Pdf/components/common/Loading';

import useGeneralStyles from 'apps/Pdf/styles/general';
import useColorStyles from 'apps/Pdf/styles/colors';
import { MeetingRecordContent } from './MeetingRecordInterface';
import QuestionAndAnswer from 'apps/Pdf/templates/AmlQuestionnaire/components/QuestionAndAnswer';
import { PersonType, AccountMode } from 'enums';
import { PdfCategory } from '../Interfaces';
import { nicerZipInAddress } from '../../helpers/helpers';

const spcIZ = 'BT';

const MeetingRecord = (params: PdfContentParams) => {
  const styles = useGeneralStyles();
  const { t } = useTranslation();
  const pdfContent = usePdfContent<MeetingRecordContent>({
    templateType: TPL_MEETING_RECORD,
    category: PdfCategory.SIGNATURE,
    ...params,
  });
  const data = pdfContent?.data ? pdfContent.data : ({} as MeetingRecordContent);
  const colors = useColorStyles({ code: pdfContent?.data?.header.signedVia || '' });
  const legal = data.aml && data.aml.personType === PersonType.LEGAL;
  const shortName = pdfContent ? t(`intermediary_${data.header.signedVia}_short`) : '';

  return pdfContent ? (
    <PdfContent errors={pdfContent?.errors} className={colors.root} onload afterLoad={params.afterLoad}>
      <Title>Záznam z jednání</Title>
      <BasicData data={data.header} />

      {data.clientData?.representative && (
        <>
          <H2>Zástupce zákazníka</H2>
          <div className={`${styles.twoColumns} ${styles.lines}`}>
            <Grid container>
              <Grid container item xs={4}>
                Jméno:
              </Grid>
              <Grid container item xs={8} className={`${styles.value}`}>
                {data.clientData.representative.firstname} {data.clientData.representative.lastname}
              </Grid>
              <Grid container item xs={4}>
                Adresa:
              </Grid>
              <Grid container item xs={8} className={`${styles.value}`}>
                {data.clientData.representative.permanentAddressCompact},{' '}
                {t(`country_${data.clientData.representative.permanentAddress.country}`)}
              </Grid>
            </Grid>
            <Grid container>
              <Grid container item xs={4}>
                Rodné číslo:
              </Grid>
              <Grid container item xs={8} className={`${styles.value}`}>
                {data.clientData.representative.foreignId || data.clientData.representative.personalNumber}
              </Grid>
              <Grid container item xs={4}>
                Email:
              </Grid>
              <Grid container item xs={8} className={`${styles.value}`}>
                {data.clientData.representative.email}
              </Grid>
              <Grid container item xs={4}>
                Telefon:
              </Grid>
              <Grid container item xs={8} className={`${styles.value}`}>
                {data.clientData.representative.phone}
              </Grid>
            </Grid>
          </div>
        </>
      )}

      {pdfContent.data!.header.signedVia !== spcIZ && (
        <>
          <H1>Povinně poskytované informace</H1>
          <P>
            Společnost {data.header.intermediaryCompany.companyName}, IČO{' '}
            {data.header.intermediaryCompany.companyNumber}, se sídlem {data.header.intermediaryCompany.headquarters},{' '}
            {data.header.intermediaryCompany.regStatement} (dále jen „{shortName}“), je na základě registrace a
            osvědčení vydaného Českou národní bankou registrovaným investičním zprostředkovatelem podle § 29 odst. 1
            zákona č. 256/2004 Sb., o podnikání na kapitálovém trhu (dále jen „ZPKT“). Společnost {shortName} vykonává
            činnost zprostředkování obchodů s investičním nástroji a poskytuje zákazníkům investiční služby přijímání
            předávání pokynů a investiční poradenství týkající se investičních nástrojů uvedených v § 29 odst. 3 ZPKT, a
            to v závislosti na oprávnění konkrétního vázaného zástupce. Společnost {shortName} poskytuje investiční
            služby prostřednictvím vázaných zástupců zapsaných do seznamu vázaných zástupců vedeného Českou národní
            bankou. Komunikace probíhá výhradně v českém jazyce.
          </P>

          {pdfContent.data!.header.signedVia !== 'ST' ? (
            <P>
              Informace, které jsou {shortName} a poradce povinni podle ZPKT klientovi před poskytnutím investiční
              služby sdělit, mu poradce sděluje ústně při prvním osobním jednání a v písemné formě jsou mu k dispozici
              na webových stránkách {shortName}:{' '}
              <a href={data.header.intermediaryCompany.informationUrl} target="_blank" rel="noopener noreferrer">
                {data.header.intermediaryCompany.informationUrl}
              </a>
            </P>
          ) : (
            <P>
              Zákazník obdržel informaci o výši pobídek (provizí) přijímaných Zprostředkovatelem v souvislosti s
              poskytováním investičních služeb.
            </P>
          )}

          {pdfContent.data!.header.signedVia === 'KZ' && (
            <P>
              Podrobnější informace o pobídkách sděluje KZ Finance zákazníkovi prostřednictvím dokumentu na adrese:{' '}
              <a
                href="http://www.kzfinance.cz/assets/files/KZ_FINANCE_Pobidky.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                http://www.kzfinance.cz/assets/files/KZ_FINANCE_Pobidky.pdf
              </a>
            </P>
          )}
        </>
      )}

      <H1>Informace o jednání</H1>
      <Grid container className={styles.lines}>
        <Grid item xs={2}>
          Datum:
        </Grid>
        <Grid item xs={4} className={styles.value}>
          {sqlDateToLabel(data.date, 'd. M. yyyy')}
        </Grid>
        <Grid item xs={2}>
          Iniciátor schůzky:
        </Grid>
        <Grid item xs={4} className={styles.value}>
          {data.header.client.companyName
            ? data.header.client.companyName
            : `${data.header.client.firstname} ${data.header.client.lastname}`}{' '}
          (zákazník)
        </Grid>
        <Grid item xs={2}>
          Čas:
        </Grid>
        <Grid item xs={4} className={styles.value}>
          {sqlDateToLabel(data.time, 'HH:mm')}
        </Grid>
        <Grid item xs={2}>
          Místo setkání:
        </Grid>
        <Grid item xs={4} className={styles.value}>
          {data.location}
        </Grid>
      </Grid>

      <H1>Náplň jednání</H1>
      <ul className={styles.regularText} style={{ textAlign: 'justify' }}>
        {pdfContent.data!.header.signedVia !== spcIZ ? (
          <li>
            Zákazníkovi byly sděleny informace o investičním zprostředkovateli (IZ) a rizicích obecně spojených s
            investováním
          </li>
        ) : (
          <li>
            Zákazníkovi byly předány informace o investičním zprostředkovateli (IZ) a odeslány v písemné podobě na
            email.
          </li>
        )}

        <li>
          Zákazník definoval své cíle a potřeby ve finančním dotazníku a odpověděl na otázky v investičním dotazníku.
        </li>
        <li>
          Zákazníkovi byl prezentován návrh investičního plánu obsahující výběr individuálního nastavení investiční
          strategie a poskytnuty informace k rizikům souvisejícím s poskytovanou investiční službou.
        </li>
        <li>
          Zákazníkovi byly vysvětleny povaha, rizika a náklady spojené s vybranou investiční službou i souvisejícími
          investičními nástroji.
        </li>
        <li>
          Zákazník odsouhlasil navrhovaný investiční plán a byla mu odeslána finální smluvní dokumentace k podpisu.
        </li>
      </ul>

      {data.note && data.note !== '' && (
        <>
          <H2>Doplňující informace</H2>
          <P>{data.note}</P>
        </>
      )}

      <div className={styles.avoidPageBreak}>
        <H2>Poskytovaná investiční služba</H2>
        {pdfContent.data!.header.signedVia !== spcIZ ? (
          <P>
            Zákazníkovi byla zprostředkována možnost uzavření smlouvy za účelem využití investiční služby Edward
            prostřednictvím aplikace Edward. Na základě odpovědí v investičním dotazníku a zákazníkem zadaných
            investičních cílů bylo aplikací Edward předvybráno investiční portfolio odpovídající celkové rizikovosti dle
            investičního dotazníku, nebo si klient vybral vzhledem ke svým osobním preferencím jednotlivé investiční
            kategorie a jejich procentuální zastoupení v portfoliu. Součástí tohoto záznamu jsou i uvedené přílohy,
            které detailněji zaznamenávají zákazníkovi odpovědi na důležité otázky, ze kterých vychází předvybraná
            investiční portfolia pro zákazníka.
          </P>
        ) : (
          <P>
            Zákazníkovi bylo poskytnuto <span className={styles.bold}>individuální investiční poradenství</span> (ve
            smyslu zákona č. 256/2004 Sb. o podnikání na kapitálovém trhu) poradcem uvedeným výše prostřednictvím
            aplikace Edward. Na základě odpovědí v investičním dotazníku a zákazníkem zadaných investičních cílů bylo
            aplikací Edward předvybráno investiční portfolio odpovídající celkové rizikovosti dle investičního
            dotazníku, nebo si klient vybral vzhledem ke svým osobním preferencím jednotlivé investiční kategorie a
            jejich procentuální zastoupení v portfoliu.. Součástí tohoto záznamu jsou i uvedené přílohy, které
            detailněji zaznamenávají zákazníkovy odpovědi na důležité otázky, ze kterých vychází předvybraná investiční
            portfolia pro daného zákazníka.
          </P>
        )}
        {data.header.mode === AccountMode.LTIP && (
          <>
            <P>Jedná se o investiční produkt s dlouhodobým investičním horizontem nejméně 10 let.</P>
            <P>
              {data.employerAllowed
                ? 'Klient souhlasí s vklady od zaměstnavatele'
                : 'Klient nesouhlasí s vklady od zaměstnavatele'}
            </P>
          </>
        )}
      </div>

      {pdfContent.data!.header.signedVia !== spcIZ && (
        <>
          {!legal && (
            <>
              <H1>Doplňující údaje k provedení AML identifikace a kontroly</H1>
              <Grid container className={styles.lines}>
                <Grid item xs={2}>
                  Pohlaví zákazníka:
                </Grid>
                <Grid item xs={4} className={styles.value}>
                  {t(`gender_${data.aml.gender}`)}
                </Grid>
                <Grid item xs={2}>
                  Státní příslušnost:
                </Grid>
                <Grid item xs={4} className={styles.value}>
                  {t(`country_${data.aml.citizenship}`)}
                </Grid>
                <Grid item xs={2}>
                  Místo narození:
                </Grid>
                <Grid item xs={4} className={styles.value}>
                  {t(`country_${data.aml.countryOfBirth}`)}, {data.aml.placeOfBirth}
                </Grid>
                <Grid item xs={2}>
                  Jiná/é země původu<sup>1</sup>:
                </Grid>
                <Grid item xs={4} className={styles.value}>
                  žádné
                </Grid>
              </Grid>

              <P className="supplement">
                <sup>1</sup> Uveďte další státy, ve kterých byl zákazník přihlášen k trvalému pobytu nebo k pobytu
                delšímu než 1 rok. Má-li zákazník více zemí původu, kontaktujte AMLP.
              </P>

              <P>
                Průkaz totožnosti:{' '}
                <strong>
                  {t(`general_idType_${data.aml.identificationDocument.type}`)} {data.aml.identificationDocument.number}
                  , platný do {sqlDateToLabel(data.aml.identificationDocument.validTo, 'dd. MM. yyyy')}, vydal{' '}
                  {data.aml.identificationDocument.issuer}
                </strong>
                <br />
                Datum provedení identifikace zákazníka za osobní přítomnosti:{' '}
                <strong>{sqlDateToLabel(data.aml.verificationDate, 'dd. MM. yyyy')}</strong>
              </P>
              <QuestionAndAnswer
                label="incomeType"
                answers={data.aml.amlQuestionnaire.options.incomeType}
                answer={data.aml.amlQuestionnaire.incomeType.type}
              />
              {data.aml.amlQuestionnaire.incomeType.type === 'THIRD_PERSON' && (
                <QuestionAndAnswer
                  label="incomeType_thirdPersonType"
                  answers={data.aml.amlQuestionnaire.options.thirdPersonIncomeType}
                  answer={data.aml.amlQuestionnaire.incomeType.thirdPersonType}
                />
              )}

              {data.aml.amlQuestionnaire.incomeType.type === 'SELF' && (
                <QuestionAndAnswer
                  label="riskBranch"
                  answers={data.aml.amlQuestionnaire.options.riskBranch}
                  answer={data.aml.amlQuestionnaire.incomeType.riskBranch}
                />
              )}
              <QuestionAndAnswer
                label="incomeBracket"
                answers={data.aml.amlQuestionnaire.options.incomeInterval}
                answer={data.aml.amlQuestionnaire.incomeInterval}
                currency={data.currency}
                currencyInAnswer={true}
              />
              <QuestionAndAnswer
                label="incomeSource"
                answers={data.aml.amlQuestionnaire.options.incomeSource}
                answer={data.aml.amlQuestionnaire.incomeSource.type}
                amount={data.aml.amlQuestionnaire.incomeSource.amount}
                currency={data.currency}
              />
              <QuestionAndAnswer
                label={`pep`}
                htmlLabel={true}
                answer={data.aml.amlQuestionnaire.pepData.politicallyExposedPerson}
                details={{
                  [`politicallyExposedDetail`]: data.aml.amlQuestionnaire.pepData.politicallyExposedPerson
                    ? data.aml.amlQuestionnaire.pepData.politicallyExposedDetail
                    : '',
                }}
              />

              <P className="supplement">
                Výše uvedené údaje o zákazníkovi je poradce povinen zjistit a zaznamenat podle zákona č. 253/2008 Sb., o
                některých opatřeních proti legalizaci výnosů z trestné činnosti a financování terorismu, ve znění
                pozdějších předpisů.
              </P>
              <P className="supplement">
                <sup>*</sup> Fyzická osoba, která je nebo byla ve významné veřejné funkci s celostátním nebo regionálním
                významem, jako je zejména hlava státu, předseda vlády, vedoucí ústředního orgánu státní správy a jeho
                zástupce (náměstek, státní tajemník), člen parlamentu, člen řídícího orgánu politické strany, vedoucí
                představitel územní samosprávy, soudce nejvyššího soudu, ústavního soudu nebo jiného nejvyššího
                justičního orgánu, proti jehož rozhodnutí obecně až na výjimky nelze použít opravné prostředky, člen
                bankovní rady centrální banky, vysoký důstojník ozbrojených sil nebo sboru, člen nebo zástupce člena,
                je-li jím právnická osoba, statutárního orgánu obchodní korporace ovládané státem, velvyslanec nebo
                vedoucí diplomatické mise, anebo fyzická osoba, která obdobnou funkci vykonává nebo vykonávala v jiném
                státě, v orgánu Evropské unie anebo v mezinárodní organizaci. Dále fyzická osoba, která je osobou
                blízkou k osobě uvedené v předchozí větě, společníkem nebo skutečným majitelem stejné právnické osoby,
                popřípadě svěřenského fondu, jako osoba uvedená v předchozí větě, nebo je o ní povinné osobě známo, že
                je v jakémkoli jiném blízkém podnikatelském vztahu s osobou uvedenou v předchozí větě, nebo je skutečným
                majitelem právnické osoby, popřípadě svěřenského fondu, o kterých je povinné osobě známo, že byly
                vytvořeny ve prospěch osoby uvedené v předchozí větě.
              </P>
            </>
          )}
        </>
      )}

      {pdfContent.data!.header.signedVia === 'HO' && (
        <>
          <H1>Informace o zpracování osobních údajů / souhlas se zpracováním osobních údajů</H1>
          <P>
            Vážený zákazníku, tímto Vám v souladu s čl. 13 Nařízení Evropského parlamentu a Rady (EU) 2016/679 ze dne
            27. dubna 2016 o ochraně fyzických osob v souvislosti se zpracováním osobních údajů a o volném pohybu těchto
            údajů a o zrušení směrnice 95/46/ES (obecné nařízení o ochraně osobních údajů – dále také jen „Nařízení
            GDPR“) poskytujeme základní informace vztahující se ke zpracování Vašich osobních údajů, které nám
            poskytujete / jste nám poskytl v souvislosti s poskytováním finančních služeb nebo které získáme / jsme
            získali později v souvislosti s poskytováním služeb Vaší osobě (dále jen „Osobní údaje“)
          </P>
          <H2>1. Správce osobních údajů</H2>
          <P>
            Správcem Osobních údajů je společnost{' '}
            <strong>holver s.r.o., Hrušovská 3203/13a, Moravská Ostrava, Ostrava 702 00, IČ: 258 60 399</strong> (dále
            jen „Správce“), kterou lze kontaktovat na e-mailové adrese: <strong>star@holver.cz</strong> a telefonním
            čísle: <strong>+420 555 131 602.</strong>
          </P>
          <P>
            Správce jmenoval pověřence pro ochranu osobních údajů, kterým je společnost{' '}
            <strong>COMPLY F&L s.r.o., Vratislavova 1/21, Vyšehrad, 128 00 Praha 2, IČ: 246 91 020</strong>, kterou lze
            kontaktovat na e-mailové adrese: <strong>dpo@complyfl.cz</strong> a telefonním čísle:
            <strong>+420 222 952 139</strong>.
          </P>
          <H2>2. Účely zpracování a právní základy pro zpracování</H2>
          <P>
            2.1. Základním účelem zpracování Osobních údajů je poskytování následujících finančních služeb, na kterých
            jsme se dohodli, konkrétně:
          </P>
          <ul>
            <li>Investiční služby</li>
            <li>Zprostředkování pojištění</li>
            <li>Zprostředkování doplňkového penzijního spoření</li>
            <li>Zprostředkování spotřebitelského úvěru</li>
            <li>Zprostředkování dalších finančních či jiných produktů (zlato, vklady, stavební spoření)</li>
          </ul>
          <P>
            Zpracování Osobních údajů je tedy nezbytné pro splnění smlouvy, na základě které jsou Vám poskytovány
            uvedené služby.
          </P>
          <P>2.2. Při poskytování služeb máme dále řadu povinností stanovených různými právními předpisy, např.:</P>
          <ul>
            <li>
              zákonem č. 253/2008 Sb., o některých opatřeních proti legalizaci výnosů z trestné činnosti a financování
              terorismu, ve znění pozdějších předpisů
            </li>
            <li>zákonem č. 256/2004 Sb., o podnikání na kapitálovém trhu, ve znění pozdějších předpisů</li>
            <li>zákonem č. 427/2011 Sb., o doplňkovém penzijním spoření, ve znění pozdějších předpisů</li>
            <li>zákonem č. 170/2018 Sb., o distribuci pojištění a zajištění, ve znění pozdějších předpisů</li>
            <li>zákonem č. 257/2016 Sb., o spotřebitelském úvěru, ve znění pozdějších předpisů</li>
          </ul>
          <P>Při plnění povinností dle těchto právních předpisů dochází také ke zpracování Osobních údajů.</P>
          <P>
            2.3. V případě, že nám prostřednictvím zaškrtnutí příslušného pole v bodě 6. tohoto dokumentu nebo jiným
            prokazatelným způsobem k tomu udělíte souhlas, budeme zpracovávat Osobní údaje i pro další konkrétní účel
            nebo účely uvedené u formulace souhlasu. Právním základem pro toto zpracování bude právě udělený souhlas.
          </P>
          <P>
            2.4. Abychom zajistili, že Vám jsou z naší strany poskytovány služby řádně, dochází také ke zpracování
            Osobních údajů a dalších informací pro účely compliance kontrol ze strany našeho externího compliance
            officera, pro účely řízení rizik ze strany našeho externího risk manažera a pro účely předcházení legalizace
            výnosů z trestné činnosti ze strany našeho externího AML officera. Právním základem pro toto zpracování je
            tak náš oprávněný zájem, který je v souladu i s Vašimi zájmy.
          </P>
          <P>
            2.5. Některé Osobní údaje zpracováváme i za účelem nabízení dalších našich služeb či produktů formou přímého
            marketingu, což je také náš oprávněný zájem.
          </P>
          <P>
            2.6. Po ukončení poskytování služeb budeme některé Osobní údaje dále zpracovávat za účelem plnění archivační
            povinnosti, čímž budeme plnit naší právní povinnost.
          </P>
          <P>
            2.7. Osobní údaje budeme po ukončení poskytování služeb zpracovávat také za účelem vyřízení Vaší případné
            reklamace, za účelem obrany v případném sporu s Vámi a za účelem vymáhání případného našeho nároku vůči Vám.
            Jedná se o náš oprávněný zájem.
          </P>
          <H2>3. Příjemci osobních údajů</H2>
          <P>3.1. Osobní údaje můžeme předávat následujícím subjektům:</P>
          <ul>
            <li>naši vázaní zástupci a další obchodní zástupci</li>
            <li>
              naše spolupracující finanční instituce (např. obchodníci s cennými papíry, investiční společnosti, banky,
              pojišťovny,
            </li>
            <li>penzijní společnosti, poskytovatelé spotřebitelských úvěrů)</li>
            <li>externí compliance officer, externí risk manažer a externí AML officer</li>
            <li>poskytovatel našeho informačního systému</li>
            <li>Česká národní banka</li>
            <li>naše pojišťovna</li>
            <li>spolupracující advokátní kancelář</li>
            <li>Finanční analytický úřad</li>
            <li>případně další státní orgány</li>
          </ul>
          <P>
            3.2. V případě, že nám k tomu udělíte souhlas budeme předávat některé Osobní údaje dalším společnostem
            tvořícím se společností holver s.r.o. koncern. Aktuální seznam společností v koncernu můžete nalézt zde:
            <a href="https://www.ettecapital.com/holding/" target="_blank" rel="noopener noreferrer">
              https://www.ettecapital.com/holding
            </a>
            .
          </P>
          <H2>4. Doba uložení osobních údajů</H2>
          <P>4.1. Osobní údaje budeme mít uloženy po dobu poskytování našich služeb.</P>
          <P>
            4.2. Po skončení poskytování služeb budeme mít Osobní údaje uloženy po dobu stanovenou příslušnými právními
            předpisy a není-li tato doba stanovena, tak po dobu 5 let od ukončení poskytování služeb.
          </P>
          <H2>5. Vaše práva</H2>
          <P>
            5.1. V souvislosti se všemi účely zpracování a při splnění podmínek stanovených Nařízením GDPR máte vždy:
          </P>
          <ul>
            <li>právo na přístup k Osobním údajům</li>
            <li>právo na opravu / výmaz Osobních údajů</li>
            <li>právo na omezení zpracování Osobních údajů</li>
            <li>právo podat stížnost u dozorového úřadu</li>
          </ul>
          <P>
            5.2. V případě zpracování Osobních údajů na základě uděleného souhlasu (viz bod 2.3 výše a bod 6. níže),
            máte právo kdykoliv tento souhlas odvolat. Odvoláním souhlasu ale není dotčena zákonnost zpracování Osobních
            údajů před jeho odvoláním.
          </P>
          <P>
            5.3. V případech zpracování Osobních údajů za účely uvedenými v bodech 2.4 a 2.7 výše máte právo vznést
            námitku proti zpracování Osobních údajů. V takovém případě bude naší povinností prokázat závažné oprávněné
            důvody pro zpracování, jinak nebudeme dále Osobní údaje zpracovávat.
          </P>
          <P>
            5.4. V případě zpracování Osobních údajů za účelem uvedeným v bodě 2.5 výše máte také právo vznést námitku
            proti tomuto zpracování Osobních údajů. V takovém případě nebudeme již Osobní údaje pro tento účel dále
            zpracovávat.
          </P>
          <P>
            5.5. Bližší informace k obsahu těchto práv i ke způsobu jejich uplatňování naleznete v komplexním
            informačním dokumentu zveřejněném na našich internetových stránkách{' '}
            <a href="https://www.holver.cz/informace-pro-klienty" target="_blank" rel="noopener noreferrer">
              www.holver.cz/informace-pro-klienty
            </a>
            .
          </P>
          <H2>6. Souhlas se zpracováním osobních údajů</H2>
          <P>
            Jméno a příjmení/Název:{' '}
            {data.header.client.companyName ? (
              <>{data.header.client.companyName}</>
            ) : (
              <>
                {data.header.client.firstname} {data.header.client.lastname}
              </>
            )}
            <br />
            Datum narození/IČ: {data.header.client.identificationNumber}
            <br />
            Bydliště/Sídlo: {nicerZipInAddress(data.header.client.permanentAddressCompact)}
            {data.header.client.permanentAddressCompact && data.header.client.permanentAddressCompact !== '' && (
              <>, {t(`country_${data.header.client.permanentAddress.country}`)}</>
            )}
            <br />
            Telefon/email: {data.header.client.phone} / {data.header.client.email}
          </P>
          <P>
            6.1.{' '}
            <strong>
              Se zpracováním Osobních údajů v rozsahu jméno, příjmení, adresa, telefonní číslo a e-mailová adresa
              spočívající v jejich předání dalším společnostem tvořících se Správcem koncern za účelem mého kontaktování
              a nabízení mi jejich produktů či služeb:
            </strong>
          </P>
          <P>
            &#9745; souhlasím <span style={{ display: 'inline-block', width: '40px' }} />
            &#9744; nesouhlasím
          </P>

          <P>Potvrzuji, že jsem výše uvedené informace obdržel / obdržela, přečetl / přečetla si je a rozumím jim.</P>
        </>
      )}

      <div className={styles.avoidPageBreak}>
        <H1>Upozornění a závěrečná prohlášení</H1>

        {pdfContent.data!.header.signedVia !== spcIZ ? (
          <>
            <P className="tight">
              Podpisem záznamu z jednání zákazník prohlašuje, že se seznámil s obsahem záznamu a potvrzuje, že v něm
              uvedené informace, které se vztahují k jeho osobě, byly zákazníkem pravdivě a úplně uvedeny a poradcem
              správně zaznamenány.
            </P>
            <P className="tight">
              Podpisem záznamu z jednání zákazník dále potvrzuje, že informace o datu, čase, místě a náplni jednání,
              které jsou v záznamu uvedeny, odpovídají skutečnému průběhu a obsahu jednání mezi ním a poradcem, při
              kterém mu byly investiční služby poskytnuty.
              <br />V případě, že záznam z jednání obsahuje nepravdivé, neúplné nebo zkreslené informace nebo údaje, je
              zákazník povinen na tyto skutečnosti poradce upozornit a nepodepisovat záznam z jednání ani smluvní
              dokumentaci poskytovatele WOOD Retail Investments a.s., obchodníka s cennými papíry.
            </P>
          </>
        ) : (
          <>
            <P className="tight">
              Uvedené údaje týkající se minulé výkonnosti mají pouze informativní charakter. Minulá výkonnost
              investičního nástroje nemůže být považována za jakýkoliv příslib výkonnosti budoucí. Veškeré údaje o
              minulé výkonnosti nezahrnují vliv poplatků a provizí, které snižují konečnou výkonnost investice. Pro
              investora je také významné měnové riziko, v jehož důsledku může hodnota investice provedená v jiné měně
              než v měně cíle vlivem výkyvů měnových kurzů v průběhu času kolísat a posilující měna cílů může mít
              negativní dopad na výsledný výnos investice. S investováním obecně je spojena určitá míra rizika. Všechny
              investice na kapitálových trzích nesou v sobě riziko kolísání hodnoty v průběhu času a riziko kapitálové
              ztráty, kdy investor nemusí dosáhnout požadovaného výnosu nebo získat zpět plnou výši investovaného
              kapitálu.
            </P>

            <P className="tight">
              Prohlašuji, že jsem obdržel/a stejnopis tohoto formuláře, že mé požadavky jsou správně zaznamenány a že
              informace, které jsem zde uvedl/a, jsou úplné a pravdivé. Potvrzuji, že můj vkládaný kapitál nepochází z
              trestné činnosti a nemá žádnou souvislost s praním špinavých peněz a/nebo s financováním terorismu.
              Prohlašuji, že jsem byl/a rovněž seznámen/a s riziky investičních nástrojů a poskytované investiční
              služby, rizikům rozumím a akceptuji je.
            </P>

            <P className="tight">
              Odborné znalosti a zkušenosti v oblasti investic, finanční zázemí, investiční cíle a rizikový profil jsou
              součástí „Investičního dotazníku“, který jsem vyplnil/a při prvním poskytnutí investiční služby, kdy jsem
              také obdržel/a „Informace pro zákazníky investičních zprostředkovatelů“, jejíž součástí jsou Informace o
              investičním zprostředkovateli, informace o zařazení zákazníka, informace o pravidlech proti střetu zájmů,
              informace o pobídkách, informace o rizicích a informace o nákladech a poplatcích. Prohlašuji, že jsem
              obdržel vyhodnocení Investičního dotazníku, ve kterém je uveden výsledek mého rizikového profilu, kterému
              odpovídá vybrané investiční portfolio. Informace týkající se zpracování osobních údajů jsou umístěny na{' '}
              <a href={data.header.intermediaryCompany.dataProtectionUrl} target="_blank" rel="noopener noreferrer">
                {data.header.intermediaryCompany.dataProtectionUrl}
              </a>
              .
            </P>
          </>
        )}
      </div>

      <Signature protocol={data.signedInfo} />
    </PdfContent>
  ) : params.preview ? (
    <Loading />
  ) : null;
};

const TPL_MEETING_RECORD: TemplateType = 'meetingRecord';

export { TPL_MEETING_RECORD, MeetingRecord };
